import {
  ArrowLeft,
  Clock,
  Layers,
  Link as LinkIcon,
  Share2,
  Undo2,
  Users,
  X,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { LoaderFunctionArgs, MetaFunction } from 'react-router';
import {
  Link,
  useLoaderData,
  useNavigate,
  useSearchParams,
} from 'react-router';
import { toast } from 'sonner';
import ExportModal from '~/components/builder/ExportModal';
import FeatureToggle from '~/components/builder/FeatureToggle';
import {
  applyPreset,
  getDependents,
  getTransitiveDependencies,
} from '~/lib/builder-logic';
import type { BuilderConfig, FeatureConfig } from '~/lib/builder-types';
import { mergeComposedFeatures, toBuilderFeatures } from '~/lib/compose-logic';
import { getAllSpecs, getSpecBySlug } from '~/lib/db/queries/specs';
import type { Spec } from '~/lib/db/schema';
import { CATEGORY_LABELS } from '~/lib/utils';

export const meta: MetaFunction = () => [
  { title: 'Compose Hybrid Spec — Specly' },
  {
    name: 'description',
    content:
      'Mix and match features from multiple specs into a custom hybrid build.',
  },
];

export async function loader({ request }: LoaderFunctionArgs) {
  const url = new URL(request.url);
  const sourcesParam = url.searchParams.get('sources') ?? '';
  const sourceSlugs = sourcesParam
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);

  const [allSpecs, sources] = await Promise.all([
    getAllSpecs(),
    Promise.all(sourceSlugs.map((slug) => getSpecBySlug(slug))),
  ]);

  const resolvedSources = sources.filter(
    (s): s is NonNullable<typeof s> => s != null,
  );

  return { allSpecs, sources: resolvedSources };
}

function getEffortEstimate(score: number): { effort: string; team: string } {
  if (score < 3) return { effort: '1–2 weeks', team: 'solo' };
  if (score < 6) return { effort: '3–5 weeks', team: '2–3 engineers' };
  if (score < 10) return { effort: '6–10 weeks', team: '3–5 engineers' };
  return { effort: '10+ weeks', team: '5+ engineers' };
}

export default function ComposePage() {
  const { allSpecs, sources } = useLoaderData<typeof loader>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const composedFeatures = useMemo(
    () =>
      mergeComposedFeatures(
        sources.map((s) => ({
          slug: s.slug,
          specFeatures: s.specFeatures.map((sf) => ({
            feature: sf.feature,
            isRequired: sf.isRequired,
            definition: sf.definition,
          })),
        })),
      ),
    [sources],
  );

  const builderFeatures = useMemo(
    () => toBuilderFeatures(composedFeatures),
    [composedFeatures],
  );

  const sourceSlugs = sources.map((s) => s.slug);

  const getInitialConfig = (): BuilderConfig => {
    const urlConfig = searchParams.get('config');
    if (urlConfig) {
      try {
        return JSON.parse(atob(urlConfig)) as BuilderConfig;
      } catch {
        // fall through
      }
    }
    return applyPreset(builderFeatures, 'standard');
  };

  const [config, setConfig] = useState<BuilderConfig>(getInitialConfig);
  const [history, setHistory] = useState<BuilderConfig[]>([]);
  const [showExport, setShowExport] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const sourceKey = sourceSlugs.join(',');

  useEffect(() => {
    setConfig((prev) => {
      const next = { ...prev, features: { ...prev.features } };
      const knownSlugs = new Set(builderFeatures.map((f) => f.feature.slug));
      for (const key of Object.keys(next.features)) {
        if (!knownSlugs.has(key)) delete next.features[key];
      }
      const defaults = applyPreset(builderFeatures, 'standard').features;
      for (const [slug, fc] of Object.entries(defaults)) {
        if (!next.features[slug]) next.features[slug] = fc;
      }
      return next;
    });
  }, [builderFeatures]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const params = new URLSearchParams();
      if (sourceKey) params.set('sources', sourceKey);
      if (Object.keys(config.features).length > 0) {
        params.set('config', btoa(JSON.stringify(config)));
      }
      navigate(`?${params}`, { replace: true });
    }, 500);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [config, sourceKey, navigate]);

  // Undo
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'z') {
        e.preventDefault();
        setHistory((prev) => {
          if (prev.length === 0) return prev;
          const next = [...prev];
          const last = next.pop()!;
          setConfig(last);
          return next;
        });
      }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const setConfigWithHistory = useCallback(
    (newConfig: BuilderConfig | ((prev: BuilderConfig) => BuilderConfig)) => {
      setConfig((prev) => {
        setHistory((h) => [...h, prev]);
        return typeof newConfig === 'function' ? newConfig(prev) : newConfig;
      });
    },
    [],
  );

  function handleUndo() {
    setHistory((prev) => {
      if (prev.length === 0) return prev;
      const next = [...prev];
      const last = next.pop()!;
      setConfig(last);
      return next;
    });
  }

  const handleToggle = useCallback(
    (slug: string, enabled: boolean) => {
      setConfigWithHistory((prev) => {
        const next = { ...prev, preset: 'custom' as const };
        if (enabled) {
          const deps = getTransitiveDependencies(slug, builderFeatures);
          const newFeatures = { ...prev.features };
          newFeatures[slug] = { ...newFeatures[slug], enabled: true };
          for (const dep of deps) {
            if (newFeatures[dep]) {
              newFeatures[dep] = { ...newFeatures[dep], enabled: true };
            }
          }
          next.features = newFeatures;
        } else {
          next.features = {
            ...prev.features,
            [slug]: { ...prev.features[slug], enabled: false },
          };
        }
        return next;
      });
    },
    [builderFeatures, setConfigWithHistory],
  );

  const handleOptionChange = useCallback(
    (slug: string, groupId: string, value: string | string[]) => {
      setConfigWithHistory((prev) => ({
        ...prev,
        preset: 'custom',
        features: {
          ...prev.features,
          [slug]: {
            ...prev.features[slug],
            options: { ...prev.features[slug].options, [groupId]: value },
          },
        },
      }));
    },
    [setConfigWithHistory],
  );

  function handleAddSource(slug: string) {
    const next = [...sourceSlugs, slug];
    const params = new URLSearchParams();
    params.set('sources', next.join(','));
    if (Object.keys(config.features).length > 0) {
      params.set('config', btoa(JSON.stringify(config)));
    }
    setShowPicker(false);
    navigate(`?${params}`);
  }

  function handleRemoveSource(slug: string) {
    const next = sourceSlugs.filter((s) => s !== slug);
    const params = new URLSearchParams();
    if (next.length > 0) params.set('sources', next.join(','));
    navigate(`?${params}`);
  }

  function handleCopyLink() {
    navigator.clipboard.writeText(window.location.href);
    setCopiedLink(true);
    toast('Link copied');
    setTimeout(() => setCopiedLink(false), 2000);
  }

  const enabledSlugs = Object.entries(config.features)
    .filter(([, fc]) => fc.enabled)
    .map(([s]) => s);

  // Incompatibilities
  const conflicts: { a: string; b: string }[] = [];
  const seen = new Set<string>();
  for (const cf of composedFeatures) {
    if (!config.features[cf.feature.slug]?.enabled) continue;
    for (const incompatSlug of cf.definition.incompatibleWith) {
      if (!config.features[incompatSlug]?.enabled) continue;
      const key = [cf.feature.slug, incompatSlug].sort().join('|');
      if (!seen.has(key)) {
        seen.add(key);
        conflicts.push({ a: cf.feature.slug, b: incompatSlug });
      }
    }
  }

  const getFeatureName = (slug: string) =>
    composedFeatures.find((cf) => cf.feature.slug === slug)?.feature.name ??
    slug;

  // Effort estimate
  const complexityScore = composedFeatures
    .filter((cf) => config.features[cf.feature.slug]?.enabled)
    .reduce((sum, cf) => {
      const w =
        cf.feature.complexity === 'high'
          ? 2
          : cf.feature.complexity === 'medium'
            ? 1.5
            : 1;
      return sum + w;
    }, 0);
  const { effort, team } = getEffortEstimate(complexityScore);

  // Missing deps: dependencies referenced by enabled features but not present
  // in the composed set — surface as a hint to add a source spec.
  const missingDeps = useMemo(() => {
    const present = new Set(composedFeatures.map((cf) => cf.feature.slug));
    const missing = new Set<string>();
    for (const cf of composedFeatures) {
      if (!config.features[cf.feature.slug]?.enabled) continue;
      for (const dep of cf.definition.dependencies) {
        if (!present.has(dep)) missing.add(dep);
      }
    }
    return [...missing];
  }, [composedFeatures, config]);

  const sourceBySlug = new Map(sources.map((s) => [s.slug, s]));

  // Build a synthetic spec object for the ExportModal
  const hybridSpec: Spec = {
    id: 'hybrid',
    slug: 'hybrid',
    name:
      sources.length === 0
        ? 'Hybrid Spec'
        : `Hybrid: ${sources.map((s) => s.name).join(' + ')}`,
    description: `Custom composition built from ${
      sources.length === 0
        ? 'no sources yet'
        : sources.map((s) => s.name).join(', ')
    }.`,
    category: sources[0]?.category ?? 'other',
    authorId: null,
    isOfficial: false,
    status: 'published',
    extractionJson: null,
    createdAt: new Date(),
    updatedAt: new Date(),
  };

  const exportFeatures = composedFeatures.map((cf) => ({
    ...cf.feature,
    definition: cf.definition,
  }));

  const enabledCount = enabledSlugs.length;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6">
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 mb-3"
        >
          <ArrowLeft className="h-4 w-4" />
          Home
        </Link>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Layers className="h-5 w-5 text-indigo-600" />
              <h1 className="text-2xl font-bold text-gray-900">
                Compose Hybrid Spec
              </h1>
            </div>
            <p className="text-sm text-gray-500 max-w-xl">
              Pick features from multiple specs and mix them into a single
              build. Notifications from Slack + payments from e-commerce? Go.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleUndo}
              disabled={history.length === 0}
              className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Undo2 className="h-4 w-4" />
              Undo
            </button>
            <button
              type="button"
              onClick={handleCopyLink}
              className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
            >
              <LinkIcon className="h-4 w-4" />
              {copiedLink ? 'Copied!' : 'Copy link'}
            </button>
            <button
              type="button"
              onClick={() => setShowExport(true)}
              disabled={sources.length === 0 || enabledCount === 0}
              className="flex items-center gap-2 px-4 py-1.5 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Share2 className="h-4 w-4" />
              Export
            </button>
          </div>
        </div>
      </div>

      {/* Source spec chips */}
      <div className="mb-6 bg-white border border-gray-200 rounded-xl p-4">
        <h2 className="text-sm font-semibold text-gray-700 mb-3">
          Source specs
        </h2>
        <div className="flex flex-wrap gap-2 items-center">
          {sources.map((s) => (
            <div
              key={s.slug}
              className="flex items-center gap-2 px-3 py-1.5 bg-indigo-50 border border-indigo-200 rounded-full text-sm"
            >
              <span className="font-medium text-indigo-800">{s.name}</span>
              <span className="text-xs text-indigo-500">
                {s.specFeatures.length} features
              </span>
              <button
                type="button"
                onClick={() => handleRemoveSource(s.slug)}
                className="text-indigo-400 hover:text-indigo-700"
                aria-label={`Remove ${s.name}`}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={() => setShowPicker((v) => !v)}
            className="px-3 py-1.5 border border-dashed border-gray-300 rounded-full text-sm text-gray-600 hover:border-indigo-300 hover:text-indigo-700 transition-colors"
          >
            + Add source
          </button>
        </div>
        {showPicker && (
          <div className="mt-3 border-t pt-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 max-h-72 overflow-auto">
              {allSpecs
                .filter((s) => !sourceSlugs.includes(s.slug))
                .map((s) => (
                  <button
                    key={s.slug}
                    type="button"
                    onClick={() => handleAddSource(s.slug)}
                    className="text-left px-3 py-2 border border-gray-200 rounded-lg hover:border-indigo-300 hover:bg-indigo-50 transition-colors"
                  >
                    <div className="text-sm font-medium text-gray-800">
                      {s.name}
                    </div>
                    <div className="text-xs text-gray-500">
                      {CATEGORY_LABELS[s.category] ?? s.category}
                    </div>
                  </button>
                ))}
              {allSpecs.filter((s) => !sourceSlugs.includes(s.slug)).length ===
                0 && (
                <p className="text-sm text-gray-400 col-span-full py-4 text-center">
                  All specs are already selected.
                </p>
              )}
            </div>
          </div>
        )}
      </div>

      {sources.length === 0 ? (
        <ComposeEmptyState
          allSpecs={allSpecs}
          onPick={(slugs) => {
            const params = new URLSearchParams();
            params.set('sources', slugs.join(','));
            navigate(`?${params}`);
          }}
        />
      ) : (
        <>
          {conflicts.length > 0 && (
            <div className="mb-4 space-y-2">
              {conflicts.map((c, i) => (
                <div
                  key={i}
                  className="flex items-center gap-2 px-4 py-2.5 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800"
                >
                  ⚠ <strong>{getFeatureName(c.a)}</strong> and{' '}
                  <strong>{getFeatureName(c.b)}</strong> are incompatible —
                  choose one.
                </div>
              ))}
            </div>
          )}

          {missingDeps.length > 0 && (
            <div className="mb-4 px-4 py-2.5 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
              Some enabled features depend on{' '}
              <strong>{missingDeps.join(', ')}</strong>, which aren't in any
              selected source spec. Add a source that includes them or pick a
              different feature.
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-2">
              {composedFeatures.map((cf) => {
                const featureConfig: FeatureConfig = config.features[
                  cf.feature.slug
                ] ?? { enabled: false, options: {} };
                const dependents = !featureConfig.enabled
                  ? []
                  : getDependents(
                      cf.feature.slug,
                      builderFeatures,
                      enabledSlugs,
                    );
                const warning =
                  !featureConfig.enabled && dependents.length > 0
                    ? `Required by: ${dependents.join(', ')}`
                    : undefined;

                return (
                  <div key={cf.feature.slug}>
                    <FeatureToggle
                      feature={{ ...cf.feature, definition: cf.definition }}
                      config={featureConfig}
                      isRequired={false}
                      dependentWarning={warning}
                      onToggle={handleToggle}
                      onOptionChange={handleOptionChange}
                    />
                    <div className="flex flex-wrap gap-1 mt-1 ml-12 mb-2">
                      <span className="text-xs text-gray-400">from</span>
                      {cf.sourceSlugs.map((slug) => {
                        const spec = sourceBySlug.get(slug);
                        return (
                          <Link
                            key={slug}
                            to={`/specs/${slug}`}
                            className="text-xs px-1.5 py-0.5 rounded-full border border-gray-200 text-gray-600 hover:bg-gray-50"
                          >
                            {spec?.name ?? slug}
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="space-y-4 sticky top-20 self-start">
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <h2 className="text-sm font-semibold text-gray-700 mb-3">
                  Summary
                </h2>
                <p className="text-sm text-gray-600 mb-3">
                  <strong>{enabledCount}</strong> of {composedFeatures.length}{' '}
                  composed features enabled
                </p>
                <p className="text-xs text-gray-500">
                  Pulled from {sources.length} source spec
                  {sources.length === 1 ? '' : 's'}
                </p>
              </div>

              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Clock className="h-4 w-4 text-gray-400" />
                  <h2 className="text-sm font-semibold text-gray-700">
                    Effort Estimate
                  </h2>
                </div>
                <p className="text-sm text-gray-800 font-medium">{effort}</p>
                <div className="flex items-center gap-1.5 text-xs text-gray-500 mt-1">
                  <Users className="h-3.5 w-3.5" />
                  {team}
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {showExport && (
        <ExportModal
          spec={hybridSpec}
          config={config}
          features={exportFeatures}
          onClose={() => setShowExport(false)}
        />
      )}
    </div>
  );
}

const COMPOSE_SUGGESTIONS: {
  title: string;
  subtitle: string;
  slugs: string[];
}[] = [
  {
    title: 'Slack + CRM',
    subtitle: 'Team chat layered with customer records',
    slugs: ['slack-like', 'crm'],
  },
  {
    title: 'Marketplace + Messaging',
    subtitle: 'Transactional platform with built-in chat',
    slugs: ['marketplace', 'slack-like'],
  },
  {
    title: 'SaaS + Payments',
    subtitle: 'B2B app with billing from day one',
    slugs: ['saas-starter', 'ecommerce'],
  },
];

function ComposeEmptyState({
  allSpecs,
  onPick,
}: {
  allSpecs: { slug: string; name: string }[];
  onPick: (slugs: string[]) => void;
}) {
  const available = new Set(allSpecs.map((s) => s.slug));
  const suggestions = COMPOSE_SUGGESTIONS.filter((s) =>
    s.slugs.every((slug) => available.has(slug)),
  );
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-8">
      <div className="text-center mb-6">
        <Layers className="h-10 w-10 text-gray-300 mx-auto mb-3" />
        <h2 className="text-lg font-semibold text-gray-800 mb-1">
          Pick at least one spec to start composing
        </h2>
        <p className="text-sm text-gray-500 max-w-md mx-auto">
          Add one or more specs to cherry-pick features across them.
        </p>
      </div>
      {suggestions.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 text-center">
            Or try one of these combos
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {suggestions.map((s) => (
              <button
                key={s.title}
                type="button"
                onClick={() => onPick(s.slugs)}
                className="text-left px-3 py-3 border border-gray-200 rounded-xl hover:border-indigo-300 hover:bg-indigo-50 transition-colors"
              >
                <div className="text-sm font-semibold text-gray-800">
                  {s.title}
                </div>
                <div className="text-xs text-gray-500 mt-0.5">{s.subtitle}</div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
